import React, { useState, useEffect, useMemo } from 'react';

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShieldHalved, faIdCard, faUser } from "@fortawesome/free-solid-svg-icons";

import { PrimaryButton, SecondaryButton } from '../../../../components/button/Button.jsx';
import { UserAuth } from '../../../../authentication/AuthContext.jsx';
import useRoleNames from '../../../../hooks/useRoleNames.js';
import { getOrgById } from '../../../../services/admin-settings/org-management/OrgRegistrationService.jsx';
import { getGameRegistries } from '../../../../services/admin-settings/GameService.jsx';

import '../user-access-registration/AccessManagementInvitationAccept.css';

const AccessManagementApproval = ({ invitation, onApprove, onDecline, approveLoading, declineLoading }) => {
    const { user } = UserAuth();
    const { roleLabel } = useRoleNames();
    const [orgName, setOrgName] = useState('');
    const [gameRegistries, setGameRegistries] = useState([]);

    const intendedEmail = invitation?.intendedTargetEmail || '';
    const requesterName = invitation?.senderUserName || invitation?.originatorUserId || '';
    const gameDetailsMap = invitation?.orgDetails?.gameDetailsMap;
    const hasGameDetails = gameDetailsMap && Object.keys(gameDetailsMap).length > 0;

    useEffect(() => {
        const fetchOrg = async () => {
            const orgId = invitation?.orgDetails?.orgId;
            if (!orgId || !user?.accessToken) return;
            try {
                const orgData = await getOrgById({ accessToken: user.accessToken, id: orgId });
                setOrgName(orgData?.org.orgName || '');
            } catch (err) {
                console.error('Failed to fetch org:', err);
            }
        };
        fetchOrg();
    }, [invitation?.orgDetails?.orgId, user?.accessToken]);

    useEffect(() => {
        if (!hasGameDetails || !user?.accessToken) return;
        const fetchGameData = async () => {
            try {
                const gamesRes = await getGameRegistries({ accessToken: user.accessToken });
                setGameRegistries(gamesRes?.games || []);
            } catch (err) {
                console.error('Failed to fetch game data:', err);
            }
        };
        fetchGameData();
    }, [hasGameDetails, user?.accessToken]);

    const gameName = useMemo(() => {
        if (!hasGameDetails) return null;
        const gameIds = Object.keys(gameDetailsMap);
        return gameIds.map(id => {
            const byId = gameRegistries.find(g => g.id === id);
            if (byId) return byId.gameName;
            const byClientId = gameRegistries.find(g =>
                g.builds?.some(group => group.gameClientId === id)
            );
            return byClientId?.gameName || id;
        }).join(', ');
    }, [hasGameDetails, gameDetailsMap, gameRegistries]);

    return (
        <div className="inv-accept-card">
            {/* ===== Header ===== */}
            <div className="inv-accept-header">
                <div className="inv-accept-header-icon-wrapper">
                    <FontAwesomeIcon icon={faShieldHalved} />
                </div>
                <div className="inv-accept-header-title-section">
                    <h2 className="inv-accept-header-title">Approve Invitation Request</h2>
                    <p className="inv-accept-header-subtitle">
                        A game owner in your organization is requesting approval to invite a new member.
                    </p>
                </div>
            </div>

            {/* ===== Scrollable Body ===== */}
            <div className="inv-accept-body">
                {/* Section: Requester Details */}
                <div className="inv-accept-section">
                    <h3 className="inv-accept-section-title">
                        <FontAwesomeIcon icon={faUser} />
                        Requester Details
                    </h3>
                    <div className="inv-accept-fields-grid">
                        <div className="inv-accept-field">
                            <span className="inv-accept-field-label">Requested By</span>
                            <span className="inv-accept-field-value">{requesterName || 'N/A'}</span>
                        </div>
                        <div className="inv-accept-field">
                            <span className="inv-accept-field-label">Organization</span>
                            <span className="inv-accept-field-value">{orgName || 'N/A'}</span>
                        </div>
                    </div>
                </div>

                <hr />

                {/* Section: Intended Target */}
                <div className="inv-accept-section">
                    <h3 className="inv-accept-section-title">
                        <FontAwesomeIcon icon={faIdCard} />
                        Invitation Target
                    </h3>
                    <div className="inv-accept-fields-grid">
                        <div className="inv-accept-field">
                            <span className="inv-accept-field-label">Email</span>
                            <span className="inv-accept-field-value">{intendedEmail || 'N/A'}</span>
                        </div>
                        <div className="inv-accept-field">
                            <span className="inv-accept-field-label">Invitation Level</span>
                            <span className="inv-accept-field-value">
                                {invitation?.invitationLevel ? roleLabel(invitation.invitationLevel) : 'N/A'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Section: Game Details (only when game details exist) */}
                {hasGameDetails && (
                    <>
                        <hr />
                        <div className="inv-accept-section">
                            <h3 className="inv-accept-section-title">
                                <FontAwesomeIcon icon={faIdCard} />
                                Game Details
                            </h3>
                            <div className="inv-accept-fields-grid">
                                <div className="inv-accept-field">
                                    <span className="inv-accept-field-label">Game</span>
                                    <span className="inv-accept-field-value">{gameName || 'N/A'}</span>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {invitation?.message && (
                    <>
                        <hr />
                        <div className="inv-accept-section">
                            <h3 className="inv-accept-section-title">
                                <FontAwesomeIcon icon={faIdCard} />
                                Message
                            </h3>
                            <p className="inv-accept-field-value">{invitation.message}</p>
                        </div>
                    </>
                )}
            </div>

            {/* ===== Footer ===== */}
            <div className="inv-accept-footer">
                <SecondaryButton onClick={onDecline} disabled={declineLoading || approveLoading}>
                    {declineLoading ? 'Declining...' : 'Decline'}
                </SecondaryButton>
                <PrimaryButton onClick={onApprove} disabled={approveLoading || declineLoading}>
                    {approveLoading ? 'Approving...' : 'Approve'}
                </PrimaryButton>
            </div>
        </div>
    );
};

export default AccessManagementApproval;
